import { AxiosProgressEvent } from 'axios'

import { uploadChunkedFile } from '@/services/upload-chunked-file'
import { uploadFile } from '@/services/upload-file'
import { transformProgressEventToNumber } from '@/utils/format-numbers'

import { FileToGetSignedUrl } from './signed-urls-queue'

export interface FileToUpload extends FileToGetSignedUrl {
  done: boolean
  url: string
  isBatch?: boolean
}

export interface UploadQueueProps {
  abortController: AbortController
  onUploadProgressByInput: (
    inputType: string,
    progress: { progress?: number; length?: number },
  ) => void
  onFileDone: (index: number) => void
}

export class UploadQueue {
  private readonly MAX_UPLOADS_PER_TIMES = 10
  private uploadingCounter = 0
  private abortController
  private onUploadProgressByInput
  private markSignedUrlAsDone

  constructor(props: UploadQueueProps) {
    this.abortController = props.abortController
    this.onUploadProgressByInput = props.onUploadProgressByInput
    this.markSignedUrlAsDone = props.onFileDone
  }

  async execute(diagnosisId: string, files: FileToUpload[]) {
    const allUploadingPromises: Promise<unknown>[] = []
    const failedPromises: Promise<unknown>[] = []

    for (const index in files) {
      const file = {
        ...files[index],
        demandId: diagnosisId,
      }

      if (file.done) {
        this.uploadProgressHandler(file)
        continue
      }

      const promise = this.uploadingCounterWrapper(
        this.makeUpload(file),
        Number(index),
      )

      allUploadingPromises.push(promise)
      promise.catch(() => failedPromises.push(promise))

      if (this.uploadingCounter < this.MAX_UPLOADS_PER_TIMES) continue

      await promise
      await this.checksForPromisesRejectAndThrow(failedPromises)
    }

    await this.checksForPromisesRejectAndThrow(allUploadingPromises)
  }

  private async checksForPromisesRejectAndThrow(promises: Promise<unknown>[]) {
    await Promise.all(promises)
  }

  private async uploadingCounterWrapper(
    promise: Promise<unknown>,
    index: number,
  ) {
    this.uploadingCounter += 1

    await promise

    this.uploadingCounter -= 1

    this.markSignedUrlAsDone(index)
  }

  private async makeUpload(currentFile: FileToUpload & { demandId: string }) {
    if (currentFile.resumable) {
      return uploadChunkedFile(currentFile.url!, currentFile.file as File, {
        onUploadProgress: (progress) =>
          this.onUploadProgressByInput(currentFile.inputType, {
            progress,
          }),
        signal: this.abortController.signal,
      })
    }

    const getLoadedProgress = this.makeProgressCounter()

    return uploadFile(
      {
        url: currentFile.url!,
        type: currentFile.type,
        file: currentFile.file,
        originalFilename: currentFile.originalFilename,
        demandId: currentFile.demandId,
      },
      {
        onUploadProgress: (progress) => {
          if (currentFile.isBatch) {
            return this.uploadProgressHandler(currentFile, {
              loaded: getLoadedProgress(progress),
            } as AxiosProgressEvent)
          }

          this.uploadProgressHandler(currentFile, progress)
        },
        signal: this.abortController.signal,
      },
    )
  }

  private makeProgressCounter() {
    let currentLoaded = 0

    return (progress: AxiosProgressEvent) => {
      const diff = progress.loaded - currentLoaded
      currentLoaded = progress.loaded

      return diff
    }
  }

  private uploadProgressHandler(
    currentFile: FileToUpload,
    progressEvent?: AxiosProgressEvent,
  ) {
    if (currentFile.isBatch) {
      return this.onUploadProgressByInput(currentFile.inputType, {
        length: progressEvent ? progressEvent.loaded : currentFile.file.size,
      })
    }
    return this.onUploadProgressByInput(currentFile.inputType, {
      progress: progressEvent
        ? transformProgressEventToNumber(progressEvent)
        : 100,
    })
  }
}
